<script setup lang="ts">
import type { Accommodation } from '../../../../../../../types/AccommodationSearch'

definePageMeta({
  middleware: ['query-state-sync', 'query-actions'],
  analyticsType: 'Campsite',
})

const siteStore = useSiteStore()
const searchStore = useSearchv2Store()
const bookingStore = useBookingStore()
const accommodationStore = useAccommodationStore()
const appStore = useAppStore()

const { data: siteData } = await useAsyncData(() => siteStore.fetchSite())

const {
  data: searchData,
  pending: searchPending,
  refresh: refreshSearchData,
} = await useLazyAsyncData(async () => {
  const response = await accommodationStore.fetchAccommodations()
  useGaSearchAccommodationEvent(response, siteData.value)
  return response
})

useNuxtApp().hooks.hookOnce('page:finish', () => {
  if (searchData.value && siteData.value && searchStore.initialLoad) {
    useGaSearchAccommodationEvent(searchData.value, siteData.value)
    useGaSearchLocationViewAccommodation(
      siteData.value?.data,
      searchData.value?.data.meta,
      searchData.value?.data.accommodations as Accommodation[]
    )
  }
})

const { breadcrumbs } = useBreadcrumbs(null, siteData.value)
structuredBreadcrumb(breadcrumbs, siteData.value)
useStructuredLodgingBusiness(siteData.value?.data)

const {
  largeImages,
  showAccommodationInfo,
  selectedAccommodation,
  TabsMaxHeight,
  tabs,
} = useCampsitePage(siteData.value)

useSetLocaleParameters(siteData.value?.data.alternatives)

useSeoMeta({
  description: () => siteData.value?.data.seo?.description,
  title: () => siteData.value?.data.seo?.title as string,
})

const AccommodationSectionA = resolveComponent('AccommodationSectionA')
</script>

<template>
  <NuxtLayout name="site-layout">
    <template #search>
      <CampsiteSearchbox
        :discount-percentage="searchData?.data.meta.minPrice.discountPercentage"
        :price="searchData?.data.meta.minPrice.sellPrice"
        :before-price="searchData?.data.meta.minPrice.original"
        :accommodation-categories="
          searchData?.data.meta.categories.filter(
            (category) => category.accommodationCount > 0
          )
        "
        :currency="searchData?.data.meta.minPrice.currency"
        @view-accommodations="scrollIntoViewWithOffset('top', 0)"
        @update-filters="
          () => {
            useUpdateQuery()
            refreshSearchData()
          }
        "
      />
    </template>
    <template #higlights>
      <VCard
        v-if="siteData?.data.teasers.filter((t: string)=> t.length).length"
        class="bg-brand-white"
      >
        <div class="text-brand-secondary-800 font-h5 mb-xs">
          {{ $t('site-higlights') }}
        </div>
        <ul class="flex flex-wrap gap-xxs">
          <li
            v-for="(teaser, idx) in (siteData?.data.teasers.filter((t: string)=> t.length))"
            :key="idx"
            class="flex flex-row gap-1"
          >
            <div>
              <Icon
                class="text-brand-success-600"
                name="material-symbols:check-rounded"
                size="26"
              />
            </div>

            <span class="text-brand-gray-500 font-p14">{{ teaser }}</span>
          </li>
        </ul>
      </VCard>
    </template>
    <template #trustpilot>
      <VCard class="bg-brand-white">
        <div class="text-brand-secondary-800 font-h5 mb-xs">
          {{ $t('trustpilot-title') }}
        </div>
        <Trustpilot class="min-w-60" />
      </VCard>
    </template>
    <template #title-section>
      <CampsiteTitle
        v-if="siteData?.data"
        :site="siteData?.data"
        :bread-crumbs="breadcrumbs"
        @show-map="appStore.showCampsiteMap = true"
        @show-reviews="appStore.showCampsiteReviews = true"
      />
    </template>
    <template #image-section>
      <!-- styling to break out of container on bellow lg screens -->
      <div
        class="relative right-1/2 lg:right-auto left-1/2 lg:left-auto -ml-[50vw] lg:ml-0 max-w-[100vw] w-screen lg:w-auto"
      >
        <ImageCarousel
          eager-load-first-image
          class="h-[224px] sm:h-[446px] lg:h-[480px]"
          sizes="100vw lg:800px"
          :naturist="siteData?.data.naturist"
          :slides="largeImages"
          gallery
        />
      </div>
    </template>
    <template #USP>
      <div class="sm:hidden ml-md">
        <USP with-title />
      </div>
      <div class="mx-auto hidden sm:block">
        <USP />
      </div>
    </template>
    <template #tabs>
      <VCard class="bg-brand-white px-0 hidden md:block">
        <VTab :default-value="appStore.selectedCampsiteTab">
          <template #triggers>
            <div
              class="flex flex-row border-b-brand-secondary-400 border-b gap-5 px-5"
              :class="tabs.length > 3 ? 'justify-around' : 'justify-start'"
            >
              <VTabTrigger
                v-for="tab in tabs"
                :key="tab.id"
                :value="tab.id"
                class="py-1 font-link-large px-md border-t border-r border-l text-h4 border-brand-secondary-400 flex-grow max-w-[13rem] basis-0 flex gap-2 relative items-center justify-center hover:text-brand-white hover:bg-brand-secondary-400"
                :class="
                  tab.id === appStore.selectedCampsiteTab
                    ? 'text-brand-white bg-brand-secondary-400'
                    : 'text-brand-secondary-400 bg-brand-white'
                "
                @on-select="appStore.selectedCampsiteTab = tab.id"
              >
                {{ tab.title }}
                <span
                  v-if="tab.icon"
                  class="bg-brand-rating-500 text-brand-white border border-brand-rating-800 shadow-custom flex items-center justify-center w-6 h-6"
                >
                  <Icon name="material-symbols:thumb-up-rounded" size="16" />
                </span>
                <Divider
                  v-show="tab.id === appStore.selectedCampsiteTab"
                  class="max-w-8 absolute bottom-0 left-1/2 -translate-x-[50%] translate-y-3 border-brand-primary-400 border-2"
                />
              </VTabTrigger>
            </div>
          </template>
          <template #content>
            <div id="tabs" class="p-md pb-0">
              <VTabContent v-if="siteData" value="description">
                <ShowMore small :max-height="TabsMaxHeight">
                  <AccommodationTabInformation
                    :facilities="siteData.data.facilities?.topFacilities"
                    :teaser-heading="siteData.data.seo?.teaserHeading"
                    :teaser-text="siteData.data.seo?.teaserText"
                    :seo-articles="siteData.data.seoArticles"
                  />
                </ShowMore>
              </VTabContent>
              <VTabContent
                v-if="siteData?.data.importantInfo.length"
                value="important-info"
              >
                <ShowMore small :max-height="TabsMaxHeight">
                  <div
                    v-for="(info, index) in siteData?.data.importantInfo"
                    :key="index"
                    class="pt-sm"
                  >
                    {{ info }}
                  </div>
                </ShowMore>
              </VTabContent>
              <VTabContent v-if="siteData" value="facilities">
                <ShowMore small :max-height="TabsMaxHeight">
                  <AccommodationTabFacilities
                    :facility-categories="
                      siteData?.data.facilities?.facilityCategories
                    "
                  />
                  <div class="py-lg">
                    <p>{{ $t('facilities.disclaimer') }}</p>
                  </div>
                </ShowMore>
              </VTabContent>
              <VTabContent v-if="siteData?.data.reviews" value="reviews">
                <AccommodationTabReviews
                  :reviews="siteData?.data.reviews"
                  :name="siteData?.data.name"
                  :image="siteData?.data.images[0]?.small"
                  @show-reviews="appStore.showCampsiteReviews = true"
                />
              </VTabContent>
            </div>
          </template>
        </VTab>
      </VCard>

      <VCard class="bg-brand-white px-0 pb-0 md:hidden">
        <ShowMore v-if="siteData?.data" class="px-md" small :max-height="250">
          <h4 class="font-h4 text-brand-secondary-700">
            {{ $t('site-tabs.information') }}
          </h4>

          <AccommodationTabInformation
            :facilities="siteData.data?.facilities?.topFacilities"
            :teaser-heading="siteData.data.seo?.teaserHeading"
            :teaser-text="siteData?.data.seo?.teaserText"
            :seo-articles="siteData.data.seoArticles"
          />
        </ShowMore>

        <Accordion
          v-if="siteData?.data.importantInfo.length"
          no-border
          no-content-margin
          :rounded="false"
          class="bg-brand-white py-sm border-b"
          :title="$t('site-tabs.importantInformation')"
        >
          <ShowMore small :max-height="TabsMaxHeight">
            <div
              v-for="(info, index) in siteData?.data.importantInfo"
              :key="index"
              class="pt-sm"
            >
              {{ info }}
            </div>
          </ShowMore>
        </Accordion>

        <Accordion
          v-if="siteData?.data"
          no-border
          :rounded="false"
          class="bg-brand-white py-sm border-b"
          :title="$t('site-tabs.facilities')"
        >
          <ShowMore small :max-height="TabsMaxHeight">
            <AccommodationTabFacilities
              :facility-categories="
                siteData?.data.facilities?.facilityCategories
              "
            />
            <div class="py-lg">
              <p>{{ $t('facilities.disclaimer') }}</p>
            </div>
          </ShowMore>
        </Accordion>

        <Accordion
          v-if="siteData?.data.reviews"
          no-border
          :rounded="false"
          class="bg-brand-white py-sm"
        >
          <template #title>
            <div class="flex gap-2">
              <span>{{ $t('site-tabs.reviews') }}</span>
              <span
                class="bg-brand-rating-500 text-brand-white border border-brand-rating-800 shadow-custom flex items-center justify-center w-6 h-6"
              >
                <Icon name="material-symbols:thumb-up-rounded" size="16" />
              </span>
            </div>
          </template>
          <AccommodationTabReviews
            :reviews="siteData?.data.reviews"
            :name="siteData?.data.name"
            :image="siteData?.data.images[0]?.small"
            @show-reviews="appStore.showCampsiteReviews = true"
          />
        </Accordion>
      </VCard>
    </template>

    <template #accommodation-section>
      <component
        :is="AccommodationSectionA"
        :search-pending="searchPending"
        :site-data="siteData"
        :search-data="searchData"
        @refresh-search-data="refreshSearchData()"
        @update-selected-accommodation="
          (accommodation:Accommodation) => {
            selectedAccommodation = accommodation
          }
        "
        @show-accommodation-info="showAccommodationInfo = true"
      />
    </template>
  </NuxtLayout>

  <LazyAccommodationWindow
    v-if="selectedAccommodation && searchData?.data.meta"
    :open="showAccommodationInfo"
    :accommodation="selectedAccommodation"
    :accommodation-id="selectedAccommodation.id"
    :meta="searchData?.data.meta"
    @book-now="
      (booking) => {
        bookingStore.initiateBooking(
          booking.accommodationId,
          booking.arrivalDate,
          booking.duration,
          siteData?.data,
          false,
          false
        )
      }
    "
    @close="
      () => {
        showAccommodationInfo = false
      }
    "
  />

  <GoogleMapsWindow
    v-if="siteData?.data"
    :open="appStore.showCampsiteMap"
    :site-marker="{
      lat: siteData?.data.latitude,
      lng: siteData?.data.longitude,
      title: siteData?.data.name,
    }"
    @close="appStore.showCampsiteMap = false"
  />
  <LazyReviewWindow
    v-if="siteData?.data"
    :id="siteData?.data.meta.site.id"
    :site="siteData?.data"
    :open="appStore.showCampsiteReviews"
    @close="appStore.showCampsiteReviews = false"
  />
</template>
